import * as React from "react";
import { Draggable, Droppable } from "react-beautiful-dnd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faV } from "../../misc/vitalograph-svg-icons";
import { ScheduleVisit } from "./VisitSchedule";
import { SessionTypes } from "../../misc/api/ApiSessionTypes";
import { useVisitScheduleContext } from "./VisitScheduleContext";
import {
    faDiary, faFvc, faSvc, faEcg, faPcf, faDLco, faMip, faMep, faSnip, faPef,
    faBloodPressure, faLungVolume, faCough, faPulseOximetry, faFeNO, faBloodGlucose, faOscillometry, faSixMinutesWalkTest, faMvv, faChallenge,
    faSurvey, faInhaler
} from "@vitalograph/web-ui";

const grid = 6;

export const getSessionContainerStyle = (isDraggingOver): React.CSSProperties => ({
    background: isDraggingOver ? "lightblue" : "#f1f2f2",
    padding: `${grid}`,
    width: `100%`,
    display: "flex",
    margin: `${grid}px 0`,
    flexWrap: "wrap"
});

export const getItemStyle = (isDragging, draggableStyle): React.CSSProperties => {
    return {
        userSelect: "none",
        padding: grid * 2,
        margin: `${grid}px ${grid}px`,
        textAlign: "right",
        background: isDragging ? "lightgreen" : "#fff",
        cursor: "pointer",
        display: "flex",

        ...draggableStyle
    };
};

export interface SessionItemsProps {
    visit: ScheduleVisit;
    visitNum: number;
    setActive: (id: string) => void;
    dropDisabled: boolean;
}

export const SessionItems = ({ visit, visitNum, setActive, dropDisabled }: SessionItemsProps) => {
    const { sessionTypes } = useVisitScheduleContext();

    const itemIcon = (sessionType: SessionTypes) => {
        switch (sessionType) {
            case SessionTypes.Fvc:
                return faFvc;
            case SessionTypes.Svc:
                return faSvc;
            case SessionTypes.Pcf:
                return faPcf;
            case SessionTypes.Ecg:
                return faEcg;
            case SessionTypes.BloodPressure:
                return faBloodPressure;
            case SessionTypes.EDiary:
                return faDiary;
            case SessionTypes.Dlco:
                return faDLco;
            case SessionTypes.Mip:
                return faMip;
            case SessionTypes.Mep:
                return faMep;
            case SessionTypes.Snip:
                return faSnip;
            case SessionTypes.LungVolumeHelium:
                return faLungVolume;
            case SessionTypes.LungVolumePleth:
                return faLungVolume;
            case SessionTypes.Cough:
                return faCough;
            case SessionTypes.PulseOximetry:
                return faPulseOximetry;
            case SessionTypes.Feno:
                return faFeNO;
            case SessionTypes.BloodGlucose:
                return faBloodGlucose;
            case SessionTypes.Oscillometry:
                return faOscillometry;
            case SessionTypes.Pef:
                return faPef;
            case SessionTypes.SixMinuteWalkTest:
                return faSixMinutesWalkTest;
            case SessionTypes.Mvv:
                return faMvv;
            case SessionTypes.Challenge:
                return faChallenge;
            case SessionTypes.SurveyAssistek:
                return faSurvey;
            case SessionTypes.SurveySGRQ:
                return faSurvey;
            case SessionTypes.InhalerTraining:
                return faInhaler;
            default:
                return faV;
        }
    };

    return (
        <Droppable
            droppableId={visit.id}
            direction="horizontal"
            isDropDisabled={dropDisabled}
        >
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    style={getSessionContainerStyle(snapshot.isDraggingOver)}
                    onClick={e => e.stopPropagation()}
                >
                    {visit.sessions.map((session, index) => {
                        return (
                            <Draggable
                                key={session.id}
                                draggableId={session.id}
                                index={index}
                            >
                                {(provided, snapshot) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        style={getItemStyle(
                                            snapshot.isDragging,
                                            provided.draggableProps.style
                                        )}
                                        onClick={e => {
                                            e.stopPropagation();
                                            setActive(session.id);
                                        }}
                                    >
                                        <span
                                            className={
                                                session.disabled ? "text-danger" : "text-primary"
                                            }
                                        >
                                            <div className="row">
                                                <div className="col-md-1">
                                                    <span {...provided.dragHandleProps}>
                                                        <i
                                                            className="fas fa-grip-vertical"
                                                            style={{ float: "left" }}
                                                        />
                                                    </span>
                                                </div>
                                                <div className="col-md-11">
                                                    <h4>
                                                        <FontAwesomeIcon
                                                            icon={itemIcon(session.sessionType) as IconProp}
                                                            size="xl"
                                                        />&nbsp;&nbsp;
                                                        <span data-automationid={session.label}>{session.label}</span>
                                                        <div>
                                                            <small>
                                                                {sessionTypes.length > 0 &&
                                                                    sessionTypes.find(
                                                                        st =>
                                                                            st.Id ===
                                                                            session.sessionType
                                                                    ).Name}
                                                            </small>
                                                        </div>
                                                    </h4>
                                                </div>
                                            </div>
                                        </span>
                                    </div>
                                )}
                            </Draggable>
                        );
                    })}
                    {provided.placeholder}
                </div>
            )}
        </Droppable>
    );
};
